/**
 * Named gesture-tuning presets.
 *
 * Centralising the recognizer's `fling`/`thresholdPx` together with the
 * consumer-facing layout constants (commit thresholds, animation durations)
 * keeps the feel consistent and makes tuning a one-line change.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/** Month calendar: horizontal swipe to page between months (carousel slide). */
export const CALENDAR_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Commit at the smaller of a fixed travel or a fraction of the grid width. */
  commitThresholdPx: 56,
  commitThresholdRatio: 0.22,
  transitionMs: 240,
  fallbackWidthPx: 300,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/** Day view: horizontal swipe to page between days (carousel slide). */
export const DAY_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Commit at the smaller of a fixed travel or a fraction of the surface width. */
  commitThresholdPx: 64,
  commitThresholdRatio: 0.22,
  transitionMs: 240,
  fallbackWidthPx: 360,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/** Entry detail: horizontal swipe to page between sibling entries of a day. */
export const ENTRY_DETAIL_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Commit at the smaller of a fixed travel or a fraction of the surface width. */
  commitThresholdPx: 64,
  commitThresholdRatio: 0.22,
  transitionMs: 240,
  fallbackWidthPx: 360,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Page-level swipe between the main sections (Today / Calendar / Stats).
 * A larger commit travel than the per-element swipes so it never fires by
 * accident while the user is interacting inside a page.
 */
export const PAGE_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  commitThresholdRatio: 0.25,
  transitionMs: 240,
  fallbackWidthPx: 390,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Toast: horizontal swipe-to-dismiss. Either direction flings the toast off
 * screen; a short drag rubber-bands and snaps back. A fling commits even below
 * the distance threshold so a quick flick dismisses cleanly.
 */
export const TOAST_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  rubberBandLimitPx: 120,
  rubberBandFactor: 0.35,
  hapticMs: 12,
  dismissAnimationMs: 220,
  cancelAnimationMs: 200,
  fallbackWidthPx: 400,
  fling: { ...STANDARD_FLING },
} as const;

/** Entry row: horizontal swipe-to-action (left = delete, right = favorite). */
export const ENTRY_ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 96,
  rubberBandLimitPx: 130,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 240,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
} as const;

/**
 * Mobile navigation drawer: drag it toward its own edge to dismiss it.
 *
 * The drawer is anchored to the left, so only a leftward drag means anything.
 * Dragging the other way has nothing to reveal — it gets a short, capped
 * rubber-band so the panel acknowledges the finger without ever detaching from
 * its edge or committing anything.
 */
export const NAV_DRAWER_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel toward the edge that commits the close on release. */
  commitThresholdPx: 72,
  /** Give on the closed-direction drag (the one that does nothing). */
  rubberBandFactor: 0.25,
  /** Hard cap on that give. */
  rubberBandLimitPx: 24,
  hapticMs: 12,
  /** Used for the backdrop fade when the panel has not been measured yet. */
  fallbackWidthPx: 280,
  /** A flick dismisses without reaching `commitThresholdPx`. */
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;
